import React from 'react';
import { BarChart3, TrendingUp, TrendingDown, Minus, CheckCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const MissionAnalytics = ({ mission }) => {
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  // Support both actualData (old) and completionData (new) formats
  const actualData = mission?.actualData || mission?.completionData;

  if (!mission || mission.status !== 'completed' || !actualData) {
    return null;
  }

  const { summary, predictedHealth, missionInfo } = mission;

  // Calculate accuracy metrics
  const calculateAccuracy = (actual, predicted) => {
    if (!actual || !predicted) return null;
    const variance = Math.abs(actual - predicted);
    const accuracy = Math.max(0, 100 - (variance / predicted) * 100);
    return accuracy.toFixed(1);
  };

  const distanceAccuracy = actualData.actualDistance && actualData.plannedDistance
    ? calculateAccuracy(actualData.actualDistance, actualData.plannedDistance)
    : null;

  const getVarianceIcon = (variance) => {
    if (variance > 0) return <TrendingUp className="text-orange-400" size={16} />;
    if (variance < 0) return <TrendingDown className="text-green-400" size={16} />;
    return <Minus className="text-gray-400" size={16} />;
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 90) return 'text-green-400';
    if (accuracy >= 75) return 'text-yellow-400';
    if (accuracy >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <div className={`${themeClasses.secondary} rounded-lg p-3 mt-2 border ${themeClasses.border}`}>
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="text-blue-400" size={18} />
        <h4 className={`text-sm font-semibold ${themeClasses.textPrimary}`}>
          Mission Analytics
        </h4>
        <CheckCircle className="text-green-400 ml-auto" size={16} />
      </div>

      <div className="space-y-2">
        {/* Distance Comparison */}
        {actualData.actualDistance != null && actualData.plannedDistance != null && (
          <div className={`${themeClasses.card} rounded p-2`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs ${themeClasses.textMuted}`}>Distance</span>
              {distanceAccuracy && (
                <span className={`text-xs font-semibold ${getAccuracyColor(distanceAccuracy)}`}>
                  {distanceAccuracy}% accurate
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className={`${themeClasses.textMuted} mb-0.5`}>Planned</div>
                <div className={`font-semibold ${themeClasses.textPrimary}`}>
                  {(actualData.plannedDistance || 0).toFixed(2)} km
                </div>
              </div>
              <div>
                <div className={`${themeClasses.textMuted} mb-0.5`}>Actual</div>
                <div className={`font-semibold ${themeClasses.textPrimary} flex items-center gap-1`}>
                  {(actualData.actualDistance || 0).toFixed(2)} km
                  {actualData.distanceVariance != null && getVarianceIcon(actualData.distanceVariance)}
                </div>
              </div>
            </div>
            {actualData.distanceVariance != null && actualData.distanceVariance !== 0 && (
              <div className={`text-xs mt-1 ${actualData.distanceVariance > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                {actualData.distanceVariance > 0 ? '+' : ''}{actualData.distanceVariance.toFixed(2)} km variance
              </div>
            )}
          </div>
        )}

        {/* Vehicle Health Comparison */}
        {missionInfo.vehicleInfo && predictedHealth && (
          <div className={`${themeClasses.card} rounded p-2`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs ${themeClasses.textMuted}`}>Vehicle Health Impact</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className={`${themeClasses.textMuted} mb-0.5`}>Pre-Mission</div>
                <div className={`font-semibold ${
                  missionInfo.vehicleInfo.health >= 80 ? 'text-green-400' :
                  missionInfo.vehicleInfo.health >= 60 ? 'text-yellow-400' :
                  missionInfo.vehicleInfo.health >= 40 ? 'text-orange-400' :
                  'text-red-400'
                }`}>
                  {missionInfo.vehicleInfo.health}%
                </div>
              </div>
              <div>
                <div className={`${themeClasses.textMuted} mb-0.5`}>Predicted Post</div>
                <div className={`font-semibold ${
                  predictedHealth >= 80 ? 'text-green-400' :
                  predictedHealth >= 60 ? 'text-yellow-400' :
                  predictedHealth >= 40 ? 'text-orange-400' :
                  'text-red-400'
                }`}>
                  {predictedHealth}%
                </div>
              </div>
            </div>
            <div className={`text-xs mt-1 ${
              (missionInfo.vehicleInfo.health - predictedHealth) > 10 ? 'text-orange-400' :
              (missionInfo.vehicleInfo.health - predictedHealth) > 5 ? 'text-yellow-400' :
              'text-green-400'
            }`}>
              {missionInfo.vehicleInfo.health - predictedHealth}% health decrease
            </div>
          </div>
        )}

        {/* Mission Summary */}
        <div className={`${themeClasses.card} rounded p-2`}>
          <div className="text-xs space-y-1">
            <div className="flex justify-between">
              <span className={themeClasses.textMuted}>Complexity:</span>
              <span className={`font-semibold ${themeClasses.textPrimary}`}>
                {summary.complexityLevel} ({summary.complexityScore}/100)
              </span>
            </div>
            <div className="flex justify-between">
              <span className={themeClasses.textMuted}>Duration:</span>
              <span className={`font-semibold ${themeClasses.textPrimary}`}>
                {summary.estimatedDuration}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={themeClasses.textMuted}>Fuel Used:</span>
              <span className={`font-semibold ${themeClasses.textPrimary}`}>
                {summary.fuelEstimate}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionAnalytics;
